//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import ArrowIcon from '../../icons/ArrowIcon';
import withZoom from '../Zoom/withZoom';
import { useConfig } from '../../Config';
import settings from '../../Config/settings';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';

type Provider = keyof typeof settings.links;

interface SolutionCardProps {
  appName: string;
  title: string;
  text: string;
  buttonLink: string;
  content: string;
  createdAt: string;
  link: string;
  imageUrl: string;
  nameLabel: string;
  isButtonDisabled?: boolean;
  isZoomedIn?: boolean;
  onViewDetails?: () => void;
}

export function getColorByTitle(title: string): string {
  switch (title.toLowerCase()) {
    case 'plan':
      return '#2EC0CB';
    case 'test':
      return '#5C6FCB';
    case 'deploy':
      return '#6FAFFD';
    case 'measure':
      return '#C7D540';
    case 'control':
      return '#D694F9';
    case 'release':
      return '#FFC766';
    default:
      return 'gray';
  }
}

function PluginCard({
  title,
  content,
  link,
  imageUrl,
  nameLabel,
  isZoomedIn,
  isButtonDisabled,
  onViewDetails,
}: SolutionCardProps) {
  const { t } = useTranslation();
  const { services } = useConfig();
  const provider = (services?.provider?.name ?? '') as Provider;

  const tagColor = (() => {
    switch (nameLabel.toLowerCase()) {
      case 'measure':
        return 'bg-[#24A148] text-white';
      case 'deploy':
        return 'bg-[#0F62FE] text-white';
      case 'plan':
        return 'bg-[#8A3FFC] text-white';
      case 'build':
        return 'bg-[#F1C21B] text-gray-900';
      default:
        return 'bg-gray-500 text-white';
    }
  })();

  return (
    <div
      id="id-plugin-cards-container"
      data-testid="plugin-cards-container"
      className="flex h-[280px] min-w-[220px] flex-1 flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#262626]"
    >
      {/* Header: Logo + Tag */}
      <div
        id="id-plugin-cards-header"
        data-testid="plugin-cards-header"
        className="mb-4 flex items-start justify-between"
      >
        <img
          src={imageUrl}
          alt={`${title} icon`}
          className="h-6 max-w-[50%] object-contain object-left"
        />
        <span
          id="id-plugin-cards-tag"
          data-testid="plugin-cards-tag"
          className={`rounded px-2 py-0.5 text-xs font-medium ${tagColor}`}
        >
          {t(nameLabel)}
        </span>
      </div>

      {/* Content: Title + Description */}
      <div
        id="id-plugin-cards-content"
        data-testid="plugin-cards-content"
        className="mb-4"
      >
        <h3 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
          {t(title)}
        </h3>
        <p className="line-clamp-6 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
          {t(content)}
        </p>
      </div>

      {/* Footer: View Details link */}
      <div
        id="id-plugin-cards-footer"
        data-testid="plugin-cards-footer"
        className="mt-auto flex justify-end pt-2"
      >
        <button
          id="card-explore-button"
          className={`text-xs text-[#0F62FE] hover:underline dark:text-[#78A9FF] ${
            isButtonDisabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer'
          }`}
          disabled={isButtonDisabled}
          onClick={onViewDetails}
        >
          {t('viewDetails')}
        </button>
      </div>
    </div>
  );
}

export default withZoom(PluginCard, 175);
